import { useLazyQuery } from '@apollo/client/react';
import * as Sentry from '@sentry/react';
import {
  Alert,
  AlertIconButton,
  Flex,
  SearchInput,
} from '@utilitywarehouse/hearth-react';
import { FormikProps, getIn, useFormikContext } from 'formik';
import { ChangeEvent, useCallback, useEffect } from 'react';

import { SPAN_SEARCH_ADDRESS } from '@constants/otel';
import { Address } from '@generated/gql/graphql';
import { useNestedFieldProps } from '@hooks/useNestedFieldProps';
import { useTrace } from '@services/otel';
import { isAbortError } from '@utils/isAbortError';
import { validatePostcode } from '@utils/validators';

import { addressFields } from '../constants';
import { formatAddress } from '../formatAddress';
import { FormValues, NestedFormValues } from '../types';
import { getAddressFieldValues, getFieldName } from '../utils';
import { ResultsField } from './ResultsField';
import { addressSearch } from './query';
import { useStore } from './store';

const getFormattedOptions = (addresses: Address[]) =>
  addresses.map((address) => ({
    label: formatAddress(address),
    value: address.id,
  }));

const hasFulfilledForm = (values: FormValues) =>
  Object.values(values).some((value) => Boolean(value));

const isFormAddressValid = (
  preselectedValues: FormValues,
  addresses: Address[]
) => {
  const hasValues = hasFulfilledForm(preselectedValues);
  if (!hasValues) {
    return false;
  }

  // Manual entry can be custom, so valid
  if (preselectedValues.manualEntry) {
    return true;
  }

  // Checks the id of the selected address against API list
  return addresses.some(({ id }) => preselectedValues.addressSelection === id);
};

const parseFormAddress = (values: FormValues): Address => {
  // manualEntry is not a valid address property
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { addressSelection, manualEntry, ...rest } = values;

  return {
    ...rest,
    id: addressSelection,
  };
};

interface AddressSearchProps<T> {
  isLoading: boolean;
  manualEntry: boolean;
  formName: T;
  onChange?(addressSelection: Address): void;
}

const AddressSearch = <T extends string>({
  isLoading,
  formName,
  manualEntry,
  onChange,
}: AddressSearchProps<T>) => {
  const { withTrace } = useTrace();
  const {
    [formName]: {
      setAddresses,
      selectedAddress,
      setSelectedAddress: setSelectedAddressInStore,
      setHasFetchedAddresses,
    },
  } = useStore();

  const [
    searchAddress,
    { loading: searchAddressLoading, error: addressLookupError, data },
  ] = useLazyQuery(addressSearch);
  const addresses: Address[] = data?.addressSearch?.addresses ?? [];

  const addressOptions = getFormattedOptions(addresses);

  const {
    values,
    errors,
    touched,
    submitCount,
    initialValues,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    validateForm,
  }: FormikProps<NestedFormValues<T>> = useFormikContext();

  const postcode = values[formName].postcode;

  const setSelectedAddress = useCallback(
    (address?: Nullable<Address>) => {
      setFieldValue(
        getFieldName(formName, 'addressSelection'),
        address?.id ?? ''
      );

      if (!address) {
        return null;
      }

      setSelectedAddressInStore(address);
      setHasFetchedAddresses(true);
      setFieldValue(getFieldName(formName, 'manualEntry'), false);

      addressFields.forEach((field) => {
        const fieldName = getFieldName(formName, field);
        if (address[field]) {
          setFieldValue(fieldName, address[field] as string);
        } else {
          setFieldValue(fieldName, '');
        }
      });

      const addressFieldValues = getAddressFieldValues(address);
      onChange && onChange(addressFieldValues as Address);

      // TODO: we likely don't need this validation fix anymore?
      setFieldTouched(getFieldName(formName, 'addressSelection'), true);
      // revalidate after a delay to ensure that values are correctly
      // set in the form state after address selection. Primarily to prevent
      // an "invalid" form state after auto-selecting the first address after
      // fetching from a post code
      setTimeout(() => {
        validateForm();
      });
    },
    [
      formName,
      onChange,
      setFieldTouched,
      setFieldValue,
      setHasFetchedAddresses,
      setSelectedAddressInStore,
      validateForm,
    ]
  );

  const executeAddressSearch = useCallback(
    async (postCode: string) => {
      const result = await searchAddress({ variables: { postCode } });
      const addresses = result.data?.addressSearch?.addresses;
      if (!addresses) return;

      // Persistent address is injected into Formik via FormikPersist
      // It should take precedence over API when page loaded
      const preselectedAddress = values[formName];
      const isPreselectedValid = isFormAddressValid(
        preselectedAddress,
        addresses
      );

      setAddresses(addresses);
      setHasFetchedAddresses(true);

      const firstAddress = isPreselectedValid
        ? parseFormAddress(preselectedAddress)
        : addresses[0];

      if (firstAddress) {
        setSelectedAddress(firstAddress);
      }
    },
    [
      formName,
      searchAddress,
      setAddresses,
      setHasFetchedAddresses,
      setSelectedAddress,
      values,
    ]
  );

  const searchAddresses = useCallback(
    (postCode: string) => {
      const postcodeValid = validatePostcode(postCode);

      if (!postcodeValid) {
        setSelectedAddress(null);
        return null;
      }

      withTrace(SPAN_SEARCH_ADDRESS, () => executeAddressSearch(postCode), {
        postcode: postCode,
      }).catch((error: unknown) => {
        // Apollo aborts superseded lookups. Swallowing keeps the resulting
        // rejection from being reported as an unhandled error.
        if (isAbortError(error)) {
          return;
        }

        Sentry.captureException(error);
      });
    },
    [executeAddressSearch, setSelectedAddress, withTrace]
  );

  const enableManualEntry = () => {
    setFieldValue(getFieldName(formName, 'manualEntry'), true);
  };

  // if the page is refreshed, the address results will be lost,
  // so search for them again if  a postcode is filled. Otherwise
  // it will require a user to clear the input and enter it again
  useEffect(() => {
    if (
      validatePostcode(postcode) &&
      postcode !== initialValues[formName].postcode
    ) {
      searchAddresses(postcode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postcode]);

  const handlePostCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const postcode = event.target.value;
    searchAddresses(postcode);
  };

  const handleSelectAddress = (addressId: string) => {
    const address = addresses.find(({ id }) => id === addressId);
    setSelectedAddress(address);
  };

  const getFieldProps = useNestedFieldProps<NestedFormValues<string>, string>({
    values,
    touched,
    errors,
    handleChange,
    handleBlur,
    submitCount,
    formName,
    isLoading: searchAddressLoading,
  });

  const postcodeProps = getFieldProps('postcode');
  return (
    <Flex direction="column" spacing="xl">
      <SearchInput
        {...postcodeProps}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          handlePostCodeChange(event);
          handleChange(event);
        }}
        onClear={() => {
          setFieldValue(getFieldName(formName, 'postcode'), '');
        }}
        label="Your postcode"
        data-testid="AddressPickerPostCode"
        hideLabel={false}
        required
      />
      {!!addressLookupError && (
        <Alert colorScheme="danger">
          Address lookup is currently unavailable. Please enter your address
          manually.
          <AlertIconButton
            label="Enter address manually"
            onClick={enableManualEntry}
            data-testid="AddressLookupFailAlert"
          />
        </Alert>
      )}
      {!addressLookupError &&
        !manualEntry &&
        postcodeProps.validationStatus !== 'invalid' && (
          <ResultsField
            formName={formName}
            name="addressSelection"
            id="address-picker-select"
            data-testid="AddressPickerSelect"
            value={
              getIn(values, `${formName}.addressSelection`, null) ||
              selectedAddress?.id ||
              ''
            }
            label="Your address"
            onChange={handleSelectAddress}
            options={addressOptions}
            loading={searchAddressLoading}
            disabled={isLoading || addressOptions.length === 0}
            error={!!addressLookupError}
          />
        )}
    </Flex>
  );
};

export { AddressSearch };
